function clean(value) {
  return String(value || '').trim();
}

export function normaliseRoute(value) {
  return clean(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function routeFamily(route) {
  const value = normaliseRoute(route);
  const match = /^([0-9]+)[A-Z]*$/.exec(value);
  return match ? match[1] : value;
}

function routesMatch(serviceRoute, vehicleRoute) {
  const service = normaliseRoute(serviceRoute);
  const vehicle = normaliseRoute(vehicleRoute);
  if (!service || !vehicle) return false;
  if (service === vehicle) return true;

  // BODS live feeds sometimes publish the trunk line rather than the exact
  // branch (for example service 20A/20B appearing as line 20). Accept family
  // matches but mark them lower confidence later.
  const serviceFam = routeFamily(service);
  const vehicleFam = routeFamily(vehicle);
  if (serviceFam && vehicleFam && serviceFam === vehicleFam) return true;

  // Handle combined line names such as 8/9 or 42C/42D where a feed publishes
  // a slash-separated line value.
  const vehicleParts = clean(vehicleRoute).toUpperCase().split(/[\/,&\s]+/).map(normaliseRoute).filter(Boolean);
  return vehicleParts.includes(service) || vehicleParts.some((part) => routeFamily(part) === serviceFam);
}

function routeConfidence(serviceRoute, vehicleRoute) {
  return normaliseRoute(serviceRoute) === normaliseRoute(vehicleRoute) ? 1 : 0.55;
}

function tokenise(value) {
  return clean(value)
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length >= 3)
    .filter((token) => !['THE', 'AND', 'VIA', 'STOP', 'STAND', 'ROAD', 'LANE', 'STREET', 'BUS', 'STATION'].includes(token));
}

function destinationScore(serviceDestination, vehicleDestination) {
  const serviceTokens = tokenise(serviceDestination);
  const vehicleTokens = tokenise(vehicleDestination);

  if (!vehicleTokens.length || !serviceTokens.length) return 0.35;

  const vehicleSet = new Set(vehicleTokens);
  const matches = serviceTokens.filter((token) => vehicleSet.has(token)).length;
  return matches / Math.max(serviceTokens.length, vehicleTokens.length, 1);
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km) {
  if (!Number.isFinite(km)) return '';
  if (km < 1) return `${Math.round(km * 1000)}m away`;
  return `${km.toFixed(1)}km away`;
}

function formatLiveMinutes(minutes) {
  if (minutes <= 1) return 'Due';
  if (minutes < 60) return `~${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `~${hours} hr ${mins} min` : `~${hours} hr`;
}

export function countRouteVehicles(service, vehicles = []) {
  const route = service?.route;
  if (!route || !Array.isArray(vehicles)) return 0;
  return vehicles.filter((vehicle) => routesMatch(route, vehicle.route || vehicle.lineRef)).length;
}

/**
 * Returns a cautious live approximation for a service at a selected stop.
 * This is not a true AVL/RTI prediction. It only uses live vehicle position,
 * route match, destination text where available, and straight-line distance.
 */
export function getLiveEstimateForService(service, stop, vehicles = []) {
  const route = normaliseRoute(service?.route);
  if (!route || !stop?.lat || !stop?.lng || !Array.isArray(vehicles) || vehicles.length === 0) return null;

  const candidates = vehicles
    .filter((vehicle) => routesMatch(route, vehicle.route || vehicle.lineRef))
    .map((vehicle) => {
      const km = haversineKm(stop.lat, stop.lng, vehicle.lat, vehicle.lng);
      const destScore = destinationScore(service.dest || service.destination, vehicle.destination);
      const hasVehicleDestination = tokenise(vehicle.destination).length > 0;
      const routeScore = routeConfidence(route, vehicle.route || vehicle.lineRef);
      return { vehicle, km, destScore, hasVehicleDestination, routeScore };
    })
    .filter((item) => Number.isFinite(item.km) && item.km <= 18)
    // Avoid confident-looking wrong-way matches. If the vehicle has a destination,
    // require at least a small destination text overlap unless it is a very close
    // vehicle on the same route family.
    .filter((item) => !item.hasVehicleDestination || item.destScore >= 0.12 || item.km <= 2.5);

  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const aScore = a.km + (1 - a.destScore) * 2.2 + (1 - a.routeScore) * 1.5;
    const bScore = b.km + (1 - b.destScore) * 2.2 + (1 - b.routeScore) * 1.5;
    return aScore - bScore;
  });

  const best = candidates[0];
  const minutes = Math.max(1, Math.round(best.km / 0.28));
  const confidence = best.routeScore === 1 && best.destScore >= 0.35
    ? 'medium'
    : best.km <= 2.5
      ? 'low'
      : 'low';

  return {
    minutes,
    label: formatLiveMinutes(minutes),
    distance: formatDistance(best.km),
    vehicleRef: best.vehicle.vehicleRef || '',
    vehicleDestination: best.vehicle.destination || '',
    confidence,
    routeMatched: best.vehicle.route || best.vehicle.lineRef || '',
    recordedAt: best.vehicle.recordedAt || '',
    vehicleLat: best.vehicle.lat,
    vehicleLng: best.vehicle.lng,
    stopLat: stop.lat,
    stopLng: stop.lng,
  };
}
